
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { 
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

interface ReviewLinkSelectorProps {
  onLinkSelected: (platform: string) => void; // Changed from url to platform
  label?: string;
  className?: string;
}

const ReviewLinkSelector = ({ 
  onLinkSelected, 
  label = "Select a review platform", 
  className = "" 
}: ReviewLinkSelectorProps) => {
  const { user } = useAuth();
  const [reviewLinks, setReviewLinks] = useState<{
    google: string;
    facebook: string;
    sprintmint: string;
    custom: {
      name: string;
      url: string;
    }
  } | null>(null);
  const [primaryReviewLink, setPrimaryReviewLink] = useState<string>("");
  const [selectedValue, setSelectedValue] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchReviewLinks();
    }
  }, [user]);

  // Auto-select primary review link when data is loaded
  useEffect(() => {
    if (reviewLinks && primaryReviewLink && !selectedValue) {
      const url = getPlatformUrl(primaryReviewLink);
      if (url) {
        setSelectedValue(primaryReviewLink);
        onLinkSelected(primaryReviewLink); // Pass platform key instead of URL
      }
    }
  }, [reviewLinks, primaryReviewLink, selectedValue]);

  const fetchReviewLinks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!user) {
        setError("User not authenticated");
        return;
      }
      
      console.log("Fetching review links for user:", user.id);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('review_links, primary_review_link')
        .eq('id', user.id)
        .single();
        
      if (error) {
        console.error('Error fetching review links:', error);
        setError(`Error fetching review links: ${error.message}`);
        return;
      }
      
      console.log("Received profile data:", data);
      
      // Set primary review link
      if (data?.primary_review_link) {
        setPrimaryReviewLink(data.primary_review_link);
      }
      
      if (data?.review_links) {
        // Parse JSON string if needed
        const parsedLinks = typeof data.review_links === 'string'
          ? JSON.parse(data.review_links)
          : data.review_links;
          
        console.log("Parsed review links:", parsedLinks);
          
        setReviewLinks({
          google: parsedLinks.google || "",
          facebook: parsedLinks.facebook || "",
          sprintmint: parsedLinks.sprintmint || parsedLinks.checkatrade || "",
          custom: {
            name: parsedLinks.custom?.name || "",
            url: parsedLinks.custom?.url || ""
          }
        });
      } else {
        console.log("No review links found in profile data");
        setReviewLinks({
          google: "",
          facebook: "",
          sprintmint: "",
          custom: {
            name: "",
            url: ""
          }
        });
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Failed to load review links. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPlatformUrl = (platform: string) => {
    if (!reviewLinks) return "";
    
    switch(platform) {
      case "google":
        return reviewLinks.google;
      case "facebook":
        return reviewLinks.facebook;
      case "sprintmint":
        return reviewLinks.sprintmint;
      case "custom":
        return reviewLinks.custom.url;
      default:
        return "";
    }
  };

  const handleSelectChange = (value: string) => {
    setSelectedValue(value);
    
    console.log(`Selected platform: ${value}`);
    
    // Pass the platform key to the parent component
    onLinkSelected(value);
  };

  // Check if any review links are configured
  const hasLinks = reviewLinks && (
    reviewLinks.google || 
    reviewLinks.facebook || 
    reviewLinks.sprintmint || 
    (reviewLinks.custom.name && reviewLinks.custom.url)
  );

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading review links...</div>;
  }

  if (error) {
    return <div className="text-sm text-red-500">{error}</div>;
  }

  if (!hasLinks) {
    return (
      <div className={`space-y-2 ${className}`}>
        {label && <Label>{label}</Label>}
        <div className="text-sm text-muted-foreground">
          No review links configured. Please visit the Review Links page to add some.
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {label && <Label>{label}</Label>}
      <Select value={selectedValue} onValueChange={handleSelectChange}>
        <SelectTrigger className="w-full bg-white">
          <SelectValue placeholder="Select a platform" />
        </SelectTrigger>
        <SelectContent className="bg-white">
          <SelectGroup>
            <SelectLabel>Review Platforms</SelectLabel>
            {reviewLinks?.google && (
              <SelectItem value="google">
                Google {primaryReviewLink === 'google' && '⭐ (Primary)'}
              </SelectItem>
            )}
            {reviewLinks?.facebook && (
              <SelectItem value="facebook">
                Facebook {primaryReviewLink === 'facebook' && '⭐ (Primary)'}
              </SelectItem>
            )}
            {reviewLinks?.sprintmint && (
              <SelectItem value="sprintmint">
                Hound {primaryReviewLink === 'sprintmint' && '⭐ (Primary)'}
              </SelectItem>
            )}
            {reviewLinks?.custom?.name && reviewLinks?.custom?.url && (
              <SelectItem value="custom">
                {reviewLinks.custom.name} {primaryReviewLink === 'custom' && '⭐ (Primary)'}
              </SelectItem>
            )}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
};

export default ReviewLinkSelector;
